import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFracas } from '../contexts/FracasContext';
import { FiveWhyAnalysis, WhyStep } from '../types/rca.types';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Save, AlertCircle } from 'lucide-react';

// Refactored Components
import { FiveWhyHeader } from '../components/rca/FiveWhyHeader';
import { FiveWhySteps } from '../components/rca/FiveWhySteps';

export const FiveWhyTool: React.FC = () => {
    const { incidentId } = useParams<{ incidentId?: string }>();
    const navigate = useNavigate();
    const { getIncidentById } = useFracas();
    const { user } = useAuth();

    const incident = incidentId ? getIncidentById(incidentId) : undefined;

    const [problemStatement, setProblemStatement] = useState(
        incident?.problemDescription || ''
    );

    const [whySteps, setWhySteps] = useState<WhyStep[]>([
        { question: 'Why did this problem occur?', answer: '', level: 1 }
    ]);

    const [rootCause, setRootCause] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const addWhyLevel = () => {
        if (whySteps.length >= 5) return;

        const lastAnswer = whySteps[whySteps.length - 1].answer;
        if (!lastAnswer.trim()) {
            setError('Please answer the current "Why?" before adding another level');
            return;
        }

        setWhySteps([
            ...whySteps,
            {
                question: `Why? (${lastAnswer})`,
                answer: '',
                level: whySteps.length + 1
            }
        ]);
        setError(null);
    };

    const removeWhyLevel = (index: number) => {
        if (whySteps.length <= 1) return;
        setWhySteps(whySteps.filter((_, i) => i !== index));
    };

    const updateAnswer = (index: number, answer: string) => {
        const updated = [...whySteps];
        updated[index].answer = answer;
        setWhySteps(updated);

        if (index < whySteps.length - 1 && answer.trim()) {
            updated[index + 1].question = `Why? (${answer})`;
            setWhySteps(updated);
        }
    };

    const handleSave = async () => {
        if (!user) {
            setError('Must be logged in to save analysis');
            return;
        }

        if (!problemStatement.trim()) {
            setError('Please enter a problem statement');
            return;
        }

        const unanswered = whySteps.find(step => !step.answer.trim());
        if (unanswered) {
            setError(`Please answer Why level ${unanswered.level}`);
            return;
        }

        if (!rootCause.trim()) {
            setError('Please identify the root cause based on your analysis');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const analysis: Omit<FiveWhyAnalysis, 'id'> = {
                incidentId: incidentId || undefined,
                problemStatement,
                whySteps,
                rootCause,
                actionable: true,
                createdBy: user.uid,
                createdByName: user.displayName || user.email || 'Unknown',
                createdAt: Timestamp.fromDate(new Date()) as unknown as Date,
                updatedAt: Timestamp.fromDate(new Date()) as unknown as Date,
                organizationId: user.organizationId || '',
            };

            await addDoc(collection(db, 'five_why_analyses'), analysis);

            if (incidentId) {
                navigate(`/fracas/${incidentId}`);
            } else {
                navigate('/rca');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save analysis');
            setSaving(false);
        }
    };

    return (
        <div className="p-8 max-w-4xl mx-auto">
            <FiveWhyHeader incidentId={incidentId} incident={incident} />

            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            <div id="five-why-diagram" className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Problem Statement</h2>
                    <textarea
                        value={problemStatement}
                        onChange={(e) => setProblemStatement(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Clearly describe the problem you're analyzing..."
                    />
                </div>

                <FiveWhySteps
                    whySteps={whySteps}
                    addWhyLevel={addWhyLevel}
                    removeWhyLevel={removeWhyLevel}
                    updateAnswer={updateAnswer}
                />

                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Identified Root Cause</h2>
                    <textarea
                        value={rootCause}
                        onChange={(e) => setRootCause(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Based on your analysis, what is the true root cause that you can address?"
                    />
                    <p className="mt-2 text-sm text-gray-500">
                        The root cause should be actionable, specific, and something you can implement corrective actions for.
                    </p>
                </div>

                <div className="flex items-center justify-end gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save Analysis'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FiveWhyTool;
